import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import BackArrow from "../../../compoment/icon/BackArrow";
import type { RootState } from "../../../redux/store";
import {
  clearFifaPlayerStats,
  isFifaWorldCup2026Station,
  readActiveStoredStation,
  readFifaPlayerProfile,
  resolveFifaCapturedImage,
  resolveFifaStationSlug,
  type FifaPlayerProfile,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";

function isFifaProfileComplete(profile: FifaPlayerProfile | null): profile is FifaPlayerProfile {
  if (!profile) return false;
  return Boolean(
    profile.clubTeam.trim() &&
    profile.firstName.trim() &&
    profile.lastName.trim() &&
    profile.jerseyNumber.trim()
  );
}

const PencilIcon = () => (
  <svg width="42" height="42" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_14004_48599)">
      <path d="M3.5 20.125V24.5H7.875L20.7783 11.5967L16.4033 7.22167L3.5 20.125ZM6.90667 22.1667H5.83333V21.0933L16.4033 10.5233L17.4767 11.5967L6.90667 22.1667ZM24.1617 6.56833L21.4317 3.83833C21.1983 3.605 20.9067 3.5 20.6033 3.5C20.3 3.5 20.0083 3.61667 19.7867 3.83833L17.6517 5.97333L22.0267 10.3483L24.1617 8.21333C24.6167 7.75833 24.6167 7.02333 24.1617 6.56833Z" fill="#33363F" />
    </g>
    <defs>
      <clipPath id="clip0_14004_48599">
        <rect width="28" height="28" fill="white" />
      </clipPath>
    </defs>
  </svg>

);

const FifaStatsChoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const uploadedImageUrl = useSelector(
    (state: RootState) => state.uploadFlow.uploadedImageUrl
  );

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);
  const locationCapturedImage = (location.state as { capturedImage?: string } | null)
    ?.capturedImage;
  const capturedImage = resolveFifaCapturedImage({
    locationImage: locationCapturedImage,
    reduxImage: uploadedImageUrl,
  });
  const fifaPlayerProfile =
    (location.state as { fifaPlayerProfile?: FifaPlayerProfile } | null)
      ?.fifaPlayerProfile || readFifaPlayerProfile();

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
      return;
    }

    if (!capturedImage) {
      navigate(`/${stationSlug}/upload-fifa-image`, { replace: true });
      return;
    }

    if (!isFifaProfileComplete(fifaPlayerProfile)) {
      navigate(`/${stationSlug}/enter-profile`, {
        replace: true,
        state: { capturedImage, stationData },
      });
    }
  }, [capturedImage, fifaPlayerProfile, isFifaFlow, navigate, stationData, stationSlug]);

  const routeState = {
    capturedImage,
    stationData,
    fifaPlayerProfile,
  };

  const handleManualEntry = () => {
    navigate(`/${stationSlug}/enter-stats`, {
      state: routeState,
    });
  };

  const handleAiAnalyze = () => {
    clearFifaPlayerStats();
    navigate(`/${stationSlug}/creating-fifa-card`, {
      state: {
        ...routeState,
        fifaStatsSource: "ai",
        fifaStatsSkipped: true,
      },
    });
  };

  if (
    !isFifaFlow ||
    !capturedImage ||
    !isFifaProfileComplete(fifaPlayerProfile)
  ) {
    return null;
  }

  return (
    <section
      className="fifaStatsChoiceSection"
      style={{ backgroundImage: `url(${FifaBg})` }}
    >
      <div className="fifaStatsChoiceInner">
        <div className="mosidaLogoWhite fifaStatsChoiceLogo mb-5">
          <img src={LOGO} alt="MOSIDA logo" className="logo" />
          <span className="badgeCreative">FIFA World Cup</span>
        </div>

        <button
          type="button"
          className="fifaStatsChoiceBack"
          onClick={() =>
            navigate(`/${stationSlug}/enter-profile`, {
              state: { capturedImage, stationData, fifaPlayerProfile },
            })
          }
        >
          <span className="me-1">
            <BackArrow />
          </span>
          Back
        </button>

        <div className="fifaStatsChoiceBody">
          <h1 className="fifaStatsChoiceHeading mb-5 pb-4">
            WOULD YOU LIKE TO
          </h1>

          <div className="fifaStatsChoiceActions">
            <button
              type="button"
              className="fifaStatsChoiceManualBtn"
              onClick={handleManualEntry}
            >
              <span className="me-1">
                <PencilIcon />
              </span>
              ENTER MY STATS MANUALLY
            </button>

            <span className="fifaStatsChoiceOr">OR</span>

            <button
              type="button"
              className="fifaStatsChoiceAiBtn"
              onClick={handleAiAnalyze}
            >
              LET OUR AI ANALYZE YOUR STATS
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FifaStatsChoice;
